import type { TimelineRow } from './types'

import React from 'react'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'

import { formatHeaderTitile } from './helpers'
import { useTimelineGridContext } from './TimelineGridContext'

export default function TimelineGridRightPane<T>({
	components: { RowContent },
}: {
	components: {
		RowContent: <T>({ row }: { row: TimelineRow<T> }) => React.ReactNode
	}
}) {
	const { t } = useTranslation()

	const {
		rows,
		viewportHeight: height,
		viewportWidth,
		ranges,
		dimmensions,
		frequency,
		getTodayPosition,
		getContentPosition,
	} = useTimelineGridContext<T>()

	return (
		<div
			className='border-base relative border-b border-solid bg-muted'
			style={{ height }}
		>
			<div className='absolute top-0 left-0 flex h-full w-full'>
				{ranges.map((range, index) => {
					const isPreLastRange = index === ranges.length - 2

					return (
						<div
							className={cn(
								'border-base h-full border-r border-solid',
								isPreLastRange && 'border-dashed'
							)}
							style={{ width: range.daysCount * dimmensions.dayWidth }}
							key={`${range.frequency}_${index}`}
						/>
					)
				})}
			</div>
			<div className='timeline-grid-rows' style={{ height }}>
				<div className='sticky top-0 z-49 flex'>
					{ranges.map((range, index) => {
						const isLastRange = index === ranges.length - 1

						return (
							<div
								key={`${frequency}_${index}`}
								className={cn(
									'border-base flex h-10 shrink-0 grow-0 items-center border-r border-b border-solid bg-secondary px-2 text-sm font-medium',
									isLastRange && 'bg-tertiary text-muted-foreground'
								)}
								style={{ width: range.daysCount * dimmensions.dayWidth }}
							>
								{formatHeaderTitile(range)}
								{isLastRange && (
									<Tooltip>
										<TooltipTrigger asChild>
											<InfoIcon className='ml-2 size-4' />
										</TooltipTrigger>
										<TooltipContent>
											{t('app.timaline.last_range')}
										</TooltipContent>
									</Tooltip>
								)}
							</div>
						)
					})}
				</div>
				{rows.map((row) => {
					const { width, ...style } = getContentPosition(row)

					return (
						<div className='relative flex h-20 items-center' key={row.id}>
							<div
								style={{
									...style,
									maxWidth: viewportWidth - (style.left + style.right),
								}}
								className={cn(
									'absolute right-0 left-0 flex h-16 justify-between border bg-background align-top',
									{
										['rounded-l']: row.start,
										['rounded-r']: row.end,
										['overflow-hidden']: !!width && width < 300,
										['px-0 py-3']: !!width && width < 24,
									}
								)}
							>
								<RowContent<T> row={row} />
							</div>
						</div>
					)
				})}
			</div>
			<div
				style={getTodayPosition()}
				className='absolute top-0 z-49 h-full w-px bg-orange-200'
			/>
		</div>
	)
}
